import { Route, Routes } from 'react-router-dom';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { RouteFallback } from './RouteFallback';
import {
  LazyRoute,
  CampaignsSkeleton,
  CompanyPeopleSkeleton,
  ComplianceApprovalsSkeleton,
  ComplianceHistorySkeleton,
  DashboardSkeleton,
  ExportsSkeleton,
  ImportSkeleton,
  JobOpportunitiesSkeleton,
  MasterDataSkeleton,
  OpportunityDetailSkeleton,
  OrgReviewSkeleton,
  OutreachSkeleton,
  OrganisationsSkeleton,
  PeopleSkeleton,
  ProfileSkeleton,
  ProjectDetailSkeleton,
  ProjectsSkeleton,
  SettingsSkeleton,
  UsersSkeleton,
} from './pageSkeletons';
import { Forbidden } from './Forbidden';
import { MovedEventDiscovery } from './MovedEventDiscovery';
import { NotFound } from './NotFound';
import { RequireAdmin, RequireAuth } from './guards';
import { lazyRoute } from './lazyRoute';

/**
 * Domain screens are split per route.
 *
 * The auth screens, the shell and the error routes stay in the main chunk because they
 * are on the critical path — everything else is fetched the first time it is visited,
 * so signing in no longer means downloading Master Data, Users and every grid.
 */
const DashboardPage = lazyRoute(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ImportPage = lazyRoute(() =>
  import('@/features/import/ImportPage').then((m) => ({ default: m.ImportPage })),
);
const EventDiscoveryPage = lazyRoute(() =>
  import('@/features/event-discovery/EventDiscoveryPage').then((m) => ({
    default: m.EventDiscoveryPage,
  })),
);
const NewDiscoveryProjectPage = lazyRoute(() =>
  import('@/features/event-discovery/NewProjectPage').then((m) => ({
    default: m.NewProjectPage,
  })),
);
const DiscoveryProjectPage = lazyRoute(() =>
  import('@/features/event-discovery/ProjectPage').then((m) => ({ default: m.ProjectPage })),
);
const ProjectsPage = lazyRoute(() =>
  import('@/features/projects/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ProjectDetailPage = lazyRoute(() =>
  import('@/features/projects/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })),
);
const PeoplePage = lazyRoute(() =>
  import('@/features/people/PeoplePage').then((m) => ({ default: m.PeoplePage })),
);
const OrganisationsPage = lazyRoute(() =>
  import('@/features/organisations/OrganisationsPage').then((m) => ({
    default: m.OrganisationsPage,
  })),
);
const CampaignsPage = lazyRoute(() =>
  import('@/features/campaigns/CampaignsPage').then((m) => ({ default: m.CampaignsPage })),
);
const JobOpportunitiesPage = lazyRoute(() =>
  import('@/features/job-opportunities/JobOpportunitiesPage').then((m) => ({
    default: m.JobOpportunitiesPage,
  })),
);
const OpportunityDetailPage = lazyRoute(() =>
  import('@/features/job-opportunities/OpportunityDetailPage').then((m) => ({
    default: m.OpportunityDetailPage,
  })),
);
const CompanyPeoplePage = lazyRoute(() =>
  import('@/features/job-opportunities/CompanyPeoplePage').then((m) => ({
    default: m.CompanyPeoplePage,
  })),
);

const PersonDetailPage = lazyRoute(() =>
  import('@/features/job-opportunities/PersonDetailPage').then((m) => ({
    default: m.PersonDetailPage,
  })),
);
const ExportsPage = lazyRoute(() =>
  import('@/features/exports/ExportsPage').then((m) => ({ default: m.ExportsPage })),
);
const OutreachPage = lazyRoute(() =>
  import('@/features/outreach/OutreachPage').then((m) => ({ default: m.OutreachPage })),
);
const ProfilePage = lazyRoute(() =>
  import('@/features/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const MasterDataPage = lazyRoute(() =>
  import('@/features/master-data/MasterDataPage').then((m) => ({ default: m.MasterDataPage })),
);
const SettingsPage = lazyRoute(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const OrgReviewPage = lazyRoute(() =>
  import('@/features/org-review/OrgReviewPage').then((m) => ({ default: m.OrgReviewPage })),
);
const UsersPage = lazyRoute(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const ApprovalConsolePage = lazyRoute(() =>
  import('@/features/compliance/ApprovalConsolePage').then((m) => ({ default: m.ApprovalConsolePage })),
);
const ApprovalHistoryPage = lazyRoute(() =>
  import('@/features/compliance/ApprovalHistoryPage').then((m) => ({ default: m.ApprovalHistoryPage })),
);
const Gallery = lazyRoute(() =>
  import('./Gallery').then((m) => ({ default: m.Gallery })),
);

/**
 * Route table.
 *
 * Public routes first, then everything behind RequireAuth, with the admin-only
 * surface behind RequireAdmin.
 *
 * `*` renders a real 404 — the legacy app redirected every unknown path to the
 * dashboard, which made broken links invisible.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* This exact path is hardcoded into the emailed reset link. Do not change it. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<RouteFallback />}>
        <Route path="/" element={<LazyRoute skeleton={<DashboardSkeleton />}><DashboardPage /></LazyRoute>} />
        <Route
          path="/import"
          element={<MovedEventDiscovery><LazyRoute skeleton={<ImportSkeleton />}><ImportPage /></LazyRoute></MovedEventDiscovery>}
        />
        <Route
          path="/event-discovery"
          element={<LazyRoute skeleton={<DashboardSkeleton />}><EventDiscoveryPage section="overview" /></LazyRoute>}
        />
        <Route
          path="/event-discovery/projects"
          element={<LazyRoute skeleton={<ImportSkeleton />}><EventDiscoveryPage section="projects" /></LazyRoute>}
        />
        <Route
          path="/event-discovery/upcoming"
          element={<LazyRoute skeleton={<ImportSkeleton />}><EventDiscoveryPage section="upcoming" /></LazyRoute>}
        />
        <Route
          path="/event-discovery/speakers"
          element={<LazyRoute skeleton={<ImportSkeleton />}><EventDiscoveryPage section="speakers" /></LazyRoute>}
        />
        <Route
          path="/event-discovery/history"
          element={<LazyRoute skeleton={<ImportSkeleton />}><EventDiscoveryPage section="history" /></LazyRoute>}
        />
        <Route
          path="/event-discovery/new"
          element={<LazyRoute skeleton={<ImportSkeleton />}><NewDiscoveryProjectPage /></LazyRoute>}
        />
        <Route
          path="/event-discovery/:projectId"
          element={<LazyRoute skeleton={<ProjectDetailSkeleton />}><DiscoveryProjectPage /></LazyRoute>}
        />
        <Route path="/projects" element={<LazyRoute skeleton={<ProjectsSkeleton />}><ProjectsPage /></LazyRoute>} />
        <Route
          path="/projects/:projectId"
          element={<LazyRoute skeleton={<ProjectDetailSkeleton />}><ProjectDetailPage /></LazyRoute>}
        />
        <Route path="/people" element={<LazyRoute skeleton={<PeopleSkeleton />}><PeoplePage /></LazyRoute>} />
        <Route
          path="/organisations"
          element={<LazyRoute skeleton={<OrganisationsSkeleton />}><OrganisationsPage /></LazyRoute>}
        />
        <Route path="/campaigns" element={<LazyRoute skeleton={<CampaignsSkeleton />}><CampaignsPage /></LazyRoute>} />
        <Route
          path="/job-opportunities"
          element={<LazyRoute skeleton={<JobOpportunitiesSkeleton />}><JobOpportunitiesPage /></LazyRoute>}
        />
        <Route
          path="/job-opportunities/:opportunityId"
          element={<LazyRoute skeleton={<OpportunityDetailSkeleton />}><OpportunityDetailPage /></LazyRoute>}
        />
        <Route
          path="/job-opportunities/:opportunityId/people"
          element={<LazyRoute skeleton={<CompanyPeopleSkeleton />}><CompanyPeoplePage /></LazyRoute>}
        />
        <Route
          path="/job-opportunities/:opportunityId/people/:personId"
          element={<LazyRoute skeleton={<CompanyPeopleSkeleton />}><PersonDetailPage /></LazyRoute>}
        />
        {/* Role-gated inside the page (admin, manager, superadmin), not by RequireAdmin. */}
        <Route path="/outreach" element={<LazyRoute skeleton={<OutreachSkeleton />}><OutreachPage /></LazyRoute>} />
        <Route path="/exports" element={<LazyRoute skeleton={<ExportsSkeleton />}><ExportsPage /></LazyRoute>} />
        <Route path="/outreach" element={<LazyRoute skeleton={<ExportsSkeleton />}><OutreachPage /></LazyRoute>} />
        <Route path="/profile" element={<LazyRoute skeleton={<ProfileSkeleton />}><ProfilePage /></LazyRoute>} />
        <Route path="/forbidden" element={<Forbidden />} />

        {/* require_admin gates 92 endpoints server-side; mirror that here. */}
        <Route element={<RequireAdmin />}>
          <Route path="/master-data" element={<LazyRoute skeleton={<MasterDataSkeleton />}><MasterDataPage /></LazyRoute>} />
          <Route
            path="/master-data/:section"
            element={<LazyRoute skeleton={<MasterDataSkeleton />}><MasterDataPage /></LazyRoute>}
          />
          <Route path="/settings" element={<LazyRoute skeleton={<SettingsSkeleton />}><SettingsPage /></LazyRoute>} />
          <Route
            path="/settings/:section"
            element={<LazyRoute skeleton={<SettingsSkeleton />}><SettingsPage /></LazyRoute>}
          />
          <Route path="/org-review" element={<LazyRoute skeleton={<OrgReviewSkeleton />}><OrgReviewPage /></LazyRoute>} />
          <Route path="/users" element={<LazyRoute skeleton={<UsersSkeleton />}><UsersPage /></LazyRoute>} />
          <Route
            path="/compliance/approvals"
            element={<LazyRoute skeleton={<ComplianceApprovalsSkeleton />}><ApprovalConsolePage /></LazyRoute>}
          />
          <Route
            path="/compliance/approvals/history"
            element={<LazyRoute skeleton={<ComplianceHistorySkeleton />}><ApprovalHistoryPage /></LazyRoute>}
          />
        </Route>

        {/* Development-only design-system gallery. */}
        {import.meta.env.DEV && <Route path="/__gallery" element={<Gallery />} />}

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
